import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Plus, Send, Trash2, Clock, CheckCircle, XCircle, Loader2, Paperclip, ToggleLeft, ToggleRight } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate } from '@/utils/dateFormat';

export default function PaymentReminders({ projectId, clientEmail = '' }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    client_email: clientEmail,
    interval_days: 7,
    email_subject: '',
    email_body: '',
    attach_statement: true,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payment-reminders?project_id=${projectId}`);
      setReminders(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) fetchReminders(); }, [projectId, fetchReminders]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/payment-reminders`, {
        project_id: projectId,
        ...form,
      });
      toast.success('Payment reminder created');
      setCreateOpen(false);
      fetchReminders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.patch(`${API}/payment-reminders/${id}/toggle`);
      setReminders(prev => prev.map(r => r.id === id ? { ...r, is_active: res.data.is_active } : r));
      toast.success(res.data.is_active ? 'Reminder activated' : 'Reminder paused');
    } catch {
      toast.error('Failed to toggle reminder');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/payment-reminders/${id}`);
      setReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Reminder deleted');
    } catch {
      toast.error('Failed to delete reminder');
    }
  };

  const handleSendNow = async (id) => {
    setSending(id);
    try {
      await axios.post(`${API}/payment-reminders/${id}/send-now`);
      toast.success('Reminder email sent!');
      fetchReminders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSending(null);
    }
  };

  const hasReminder = reminders.length > 0;

  return (
    <div className="space-y-3" data-testid="payment-reminders-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
          <Bell size={15} className="text-amber-600" />
          Scheduled Payment Reminders
        </h3>
        {!hasReminder && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => { setForm(f => ({ ...f, client_email: clientEmail })); setCreateOpen(true); }}
            data-testid="create-reminder-btn"
          >
            <Plus size={13} /> Set Up Reminder
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-stone-400 text-sm py-3">
          <Loader2 size={14} className="animate-spin" /> Loading...
        </div>
      ) : !hasReminder ? (
        <p className="text-xs text-stone-400 py-2">No payment reminders set. Create one to auto-send payment requests to clients.</p>
      ) : (
        <div className="space-y-2">
          {reminders.map((r) => (
            <div key={r.id} className={`border rounded-lg p-3 ${r.is_active ? 'border-amber-200 bg-amber-50/30' : 'border-stone-200 bg-stone-50/50'}`} data-testid={`reminder-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {r.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                        <CheckCircle size={11} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 bg-stone-200 px-1.5 py-0.5 rounded">
                        <BellOff size={11} /> Paused
                      </span>
                    )}
                    <span className="text-xs text-stone-500">Every {r.interval_days} days</span>
                    {r.attach_statement && (
                      <span className="inline-flex items-center gap-1 text-xs text-stone-400">
                        <Paperclip size={10} /> Statement
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-600 truncate">To: {r.client_email || 'Client email'}</p>
                  {r.last_sent_at && (
                    <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1">
                      <Clock size={10} /> Last sent: {formatDate(new Date(r.last_sent_at))} ({r.send_count || 0} sent)
                    </p>
                  )}
                  {r.history && r.history.length > 0 && (
                    <div className="mt-1.5 flex gap-1 flex-wrap">
                      {r.history.slice(-5).map((h, i) => (
                        <span key={i} className={`text-[10px] px-1 py-0.5 rounded ${h.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {formatDate(new Date(h.sent_at))} {h.status === 'sent' ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => handleSendNow(r.id)}
                    disabled={sending === r.id}
                    title="Send now"
                    data-testid={`send-now-${r.id}`}
                  >
                    {sending === r.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => handleToggle(r.id)}
                    title={r.is_active ? 'Pause' : 'Activate'}
                    data-testid={`toggle-reminder-${r.id}`}
                  >
                    {r.is_active ? <ToggleRight size={15} className="text-green-600" /> : <ToggleLeft size={15} className="text-stone-400" />}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(r.id)}
                    title="Delete"
                    data-testid={`delete-reminder-${r.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Reminder Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell size={18} className="text-amber-600" />
              Set Up Payment Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-medium text-stone-500">Client Email</Label>
              <Input
                value={form.client_email}
                onChange={(e) => setForm(f => ({ ...f, client_email: e.target.value }))}
                placeholder="client@email.com"
                className="h-9"
                data-testid="reminder-client-email"
              />
              <p className="text-xs text-stone-400 mt-0.5">Leave empty to use project client's email</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-stone-500">Send Every (days)</Label>
              <Input
                type="number"
                value={form.interval_days}
                onChange={(e) => setForm(f => ({ ...f, interval_days: parseInt(e.target.value) || 7 }))}
                min={1}
                max={90}
                className="h-9 w-24"
                data-testid="reminder-interval"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-stone-500">Custom Subject <span className="text-stone-400">(optional)</span></Label>
              <Input
                value={form.email_subject}
                onChange={(e) => setForm(f => ({ ...f, email_subject: e.target.value }))}
                placeholder="Leave empty for default"
                className="h-9"
                data-testid="reminder-subject"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-stone-500">Custom Body <span className="text-stone-400">(optional)</span></Label>
              <Textarea
                value={form.email_body}
                onChange={(e) => setForm(f => ({ ...f, email_body: e.target.value }))}
                placeholder="Leave empty for auto-generated content with payment details"
                className="min-h-[80px] text-sm"
                data-testid="reminder-body"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="reminder-attach"
                checked={form.attach_statement}
                onCheckedChange={(v) => setForm(f => ({ ...f, attach_statement: v }))}
                data-testid="reminder-attach-statement"
              />
              <label htmlFor="reminder-attach" className="text-sm text-stone-700 cursor-pointer">
                Attach Customer Statement PDF
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                data-testid="save-reminder-btn"
              >
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Bell size={14} /> Create Reminder</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
